import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

/**
 * Table displaying slices that can be applied to other configurations.
 * Allows selection/deselection of individual slices.
 *
 * @param {Array} allSlices - All slices from form data (for index lookup)
 * @param {Array} copyableSlices - Array of slice objects that can be copied
 * @param {boolean} disabled - Whether selection is disabled
 * @param {() => void} onToggleAll - Callback to toggle all slices
 * @param {(sliceId: string) => void} onToggleSlice - Callback to toggle slice selection
 * @param {Map<string, string>} originalSliceNames - Map of slice ID -> original name
 * @param {Set<string>} selectedSlices - Set of selected slice IDs
 * @returns {JSX.Element}
 */
function SlicesToApplyTable({
    allSlices,
    copyableSlices,
    disabled,
    onToggleAll,
    onToggleSlice,
    originalSliceNames,
    selectedSlices,
}) {
    const allSelected = copyableSlices.length > 0 && copyableSlices.every((s) => selectedSlices.has(s.id));

    return (
        <div className="w-88 shrink-0 border rounded-lg overflow-hidden flex flex-col">
            <Table className="table-fixed [&_td]:py-2 [&_th]:py-2">
                <TableHeader className="bg-muted/50">
                    <TableRow>
                        <TableHead className="w-10">
                            <Checkbox
                                checked={allSelected}
                                onCheckedChange={onToggleAll}
                                disabled={disabled || copyableSlices.length === 0}
                            />
                        </TableHead>
                        <TableHead className="w-14 text-center">Pos</TableHead>
                        <TableHead>
                            <span className="flex items-center justify-between">
                                <span>Slice</span>
                                <span className="text-xs text-muted-foreground font-normal">
                                    {selectedSlices.size}/{copyableSlices.length}
                                </span>
                            </span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
            </Table>
            <div className="flex-1 overflow-y-auto">
                <Table className="table-fixed [&_td]:py-2 [&_th]:py-2">
                    <TableBody>
                        {copyableSlices.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} className="text-center text-muted-foreground">
                                    No named slices to apply
                                </TableCell>
                            </TableRow>
                        ) : (
                            copyableSlices.map((slice) => {
                                const originalName = originalSliceNames.get(slice.id);
                                const nameChanged = slice.name !== originalName;
                                const sliceIndex = allSlices?.findIndex((s) => s.id === slice.id) ?? -1;

                                return (
                                    <TableRow
                                        key={slice.id}
                                        className="cursor-pointer"
                                        onClick={() => !disabled && onToggleSlice(slice.id)}
                                    >
                                        <TableCell className="w-10">
                                            <Checkbox
                                                checked={selectedSlices.has(slice.id)}
                                                onCheckedChange={() => onToggleSlice(slice.id)}
                                                disabled={disabled}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </TableCell>
                                        <TableCell className="w-14 text-center text-muted-foreground">
                                            {sliceIndex + 1}
                                        </TableCell>
                                        <TableCell className="max-w-0">
                                            <div className="text-sm truncate" title={slice.name}>
                                                {slice.name}
                                            </div>
                                            {nameChanged && (
                                                <div
                                                    className="text-xs text-muted-foreground truncate"
                                                    title={`Was: ${originalName}`}
                                                >
                                                    Was: {originalName}
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

export default SlicesToApplyTable;
